import React from 'react';
import BaseClass from 'components/BaseClass';
import LayoutCard from 'components/FrictionlessCheckout/LayoutCard/LayoutCard';
import { wrapComponent } from 'utils/framework';
import {
    Box, Text, Icon, Flex
} from 'components/ui';
import PaymentsDisplay from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentsDisplay/PaymentsDisplay';
import PaymentMethodList from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList';
import NewUserPaymentView from 'components/FrictionlessCheckout/PaymentReleaseFour/NewUserPaymentView';
import CreditCardArea from 'components/FrictionlessCheckout/PaymentReleaseFour/CreditCard/CreditCardArea';
import { AfterPayExpandedView } from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/AfterPay';
import { KlarnaExpandedView } from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/Klarna';
import { VenmoExpandedView } from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/Venmo';
import PazeExpandedView from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/Paze/PazeExpandedView';
import PayPalExpandedView from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/Paypal/PayPalExpandedView';
import { GooglePayExpandedView } from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/GooglePay';
import anaConsts from 'analytics/constants';
import FrictionlessCheckoutBindings from 'analytics/bindingMethods/pages/FrictionlessCheckout/FrictionlessCheckoutBindings';
import StringUtils from 'utils/String';
import {
    SECTION_NAMES, FC_CHECKOUT_SECTIONS, FC_CHECKOUT_PATH, MAX_CARDS_LIMIT
} from 'constants/frictionlessCheckout';
import OrderUtils from 'utils/Order';
import FrictionlessUtils from 'utils/FrictionlessCheckout';
import { PAYMENT_TYPES } from 'constants/RwdBasket';
import Venmo from 'utils/Venmo';
import agentAwareUtils from 'utils/AgentAware';
import checkoutUtils from 'utils/Checkout';
import deepEqual from 'deep-equal';
import { createKlarnaWidgetLoader } from 'components/FrictionlessCheckout/PaymentReleaseFour/utils/AlternatePaymentWidgetHelpers';
import ApplePay from 'services/ApplePay';
import Empty from 'constants/empty';
import PayPal from 'utils/PayPal';
import PayPalCheckout from 'utils/PayPalCheckout';
import Location from 'utils/Location';
import { refreshCheckout } from 'services/Checkout';
import paymentUtils from 'utils/payment';
import creditCardUtils from 'utils/CreditCard';
import ErrorsUtils from 'utils/Errors';
import localeUtils from 'utils/LanguageLocale';
import GooglePayEN from 'utils/locales/GooglePay_en_US';
import GooglePayFR from 'utils/locales/GooglePay_fr_CA';

class Payment extends BaseClass {
    state = {
        isHigherRedeemPoints: this.props.zeroDollarHigherRedeemPointsNotCVV,
        openCreditCardForm: false,
        shouldAutoExpandAfterpay: false,
        isAfterPayExpanded: false,
        isAfterPayLoading: false,
        afterpayApiData: null,
        afterpayError: null,
        shouldAutoExpandKlarna: false,
        isKlarnaExpanded: false,
        isKlarnaLoading: false,
        klarnaApiData: null,
        klarnaError: null,
        useMyShippingAddress: true,
        shouldAutoExpandPayPal: false,
        shouldAutoExpandPaze: false,
        shouldAutoExpandVenmo: false,
        shouldAutoExpandGooglePay: false,
        previousPaymentSelection: null,
        isGiftCardFormOpenOnL1: false,
        suppressAutoOpenAfterExternalExit: false,
        useBraintreePayPalCheckout: false,
        hasSelectedExpiredCreditCard: false
    };

    handleExpiredSelectionChange = isExpired => {
        if (this.state.hasSelectedExpiredCreditCard !== isExpired) {
            this.setState({ hasSelectedExpiredCreditCard: isExpired });
        }
    };

    isNewUserWithVenmoExpress = false;
    didHandleVenmoExpressAutoClose = false;

    // Stop handlePaymentCheckOnExitEditView from resetting the alternate payment
    // after Save/Cancel since props may still be stale during isEditViewFromURL update.
    exitingViaCloseEditState = false;

    // Tracks if the user clicked "Change" to enter edit mode (vs entering edit mode automatically, like after saving an address).
    userInitiatedEditMode = false;

    setEditChangeLink = (isEditMode, _isTriggeredByUserClick = false) => {
        if (isEditMode) {
            if (this.state.suppressAutoOpenAfterExternalExit) {
                this.setState({ suppressAutoOpenAfterExternalExit: false });
            }

            this.userInitiatedEditMode = true;
            FrictionlessCheckoutBindings.resetPaymentButtonStateTracking();
            this.props.getCreditCards(this.props.orderId);
            this.props.goToURL({ path: `${FC_CHECKOUT_PATH}/${FC_CHECKOUT_SECTIONS.PAYMENT.path}` });
            this.props.setPaymentMethodNeedsConfirmation(true);
        }
    };

    /**
     * Fires a LINK_TRACKING_EVENT (s.tl() TrackAction) for payment vendor interactions.
     * Used for tracking express payment clicks from L1.
     *
     * @param {string} vendor - The vendor segment appended to CHECKOUT_EXPRESS_PAYMENT for prop55 (e.g., 'afterpay' → 'checkout:express-payment:afterpay')
     */
    processPaymentClickEvent = (vendor, { isExpressPayment = true } = {}) => {
        const prefix = isExpressPayment ? anaConsts.ACTION_INFO.CHECKOUT_EXPRESS_PAYMENT : anaConsts.ACTION_INFO.CHECKOUT_PAYMENT;
        FrictionlessCheckoutBindings.trackPaymentVendorInteraction(`${prefix}:${vendor}`);
    };

    handleAddNewCard = () => {
        const { paymentOptions, localization, showInfoModal } = this.props;
        const savedCardsCount = paymentOptions?.creditCards?.length || 0;

        // Check if user has reached the maximum number of saved cards
        if (savedCardsCount >= MAX_CARDS_LIMIT) {
            showInfoModal({
                isOpen: true,
                title: localization.maximumReached,
                message: localization.maxCardsReachedMessage,
                buttonText: localization.ok,
                showCloseButton: true
            });

            return;
        }

        this.setState({ openCreditCardForm: true });
    };

    handleCloseCreditCardForm = () => {
        this.setState({ openCreditCardForm: false });
    };

    handleNewUserCreditCardSave = () => {
        this.handleCloseCreditCardForm();
        this.closeEditState();
    };

    /**
     * When user clicks Afterpay pill in L1 (PaymentsDisplay) or NewUserPaymentView,
     * opens the Payment edit view (L2) and triggers auto-expansion to L3.
     * This handles AfterPay expansion at the Payment level to work for all user types.
     */
    handleAfterPayExpandFromL1 = ({ isExpressPayment = true } = {}) => {
        this.processPaymentClickEvent('afterpay', { isExpressPayment });

        this.setEditChangeLink(true, true);
        this.handleAfterPayExpand();
    };

    /**
     * Handles a PayPal pill click from the L1 state. Honors the useBraintreePayPalCheckout
     * killswitch: when TRUE, opens the L2 Payment edit view and triggers PayPal auto-expansion;
     * when FALSE, opens the legacy PayPal popup directly (same flow as handlePayPalPillClick in
     * PaymentMethodList).
     */
    handlePayPalExpandFromL1 = ({ isExpressPayment = true } = {}) => {
        if (OrderUtils.containsRestrictedItem() && !(this.props.orderHasPhysicalGiftCard && paymentUtils.isPayPalGCPurchaseEnabled())) {
            this.props.setCheckoutSectionErrors({ paymentMethod: this.props.localization.paypalRestrictedItemError });

            return;
        }

        this.processPaymentClickEvent('paypal', { isExpressPayment });

        const { useBraintreePayPalCheckout } = this.state;

        if (useBraintreePayPalCheckout) {
            // NEW: When killswitch is TRUE, open the L2 edit view and auto-expand to L3
            this.setEditChangeLink(true, true);
            this.setState({ shouldAutoExpandPayPal: true });
        } else {
            // LEGACY PAYPAL: When killswitch is FALSE, open the PayPal popup directly without
            // entering L2/L3. On successful popup completion, handlePayPalPopupComplete
            // finalizes selection and returns the user to L1.
            PayPal.showPayPal(async payload => {
                try {
                    await this.props.payPalWithInterstice(payload);
                    await this.handlePayPalPopupComplete();
                } catch (err) {
                    Sephora.logger.error(err);
                }
            });
        }
    };

    /**
     * Opens Payment edit view (L2) then triggers Paze L3 expansion.
     * Called when user clicks Paze pill from L1 state.
     */
    handlePazeExpandFromL1 = ({ isExpressPayment = true } = {}) => {
        this.processPaymentClickEvent('paze', { isExpressPayment });

        this.setEditChangeLink(true, true);
        this.setState({ shouldAutoExpandPaze: true });
    };

    /**
     * Opens Payment edit view (L2) then triggers Google Pay L3 expansion.
     * Called when the guest/new user clicks the Google Pay pill from the L1
     * NewUserPaymentView. Mirrors handlePazeExpandFromL1 — renderEditView renders
     * GooglePayExpandedView at the Payment level once shouldAutoExpandGooglePay flips true.
     */
    handleGooglePayExpandFromL1 = ({ isExpressPayment = true } = {}) => {
        this.processPaymentClickEvent('googlepay', { isExpressPayment });

        this.setEditChangeLink(true, true);
        this.setState({ shouldAutoExpandGooglePay: true });
    };

    // Collapses back to L2 without selecting Google Pay.
    clearAutoExpandGooglePay = () => {
        this.setState({ shouldAutoExpandGooglePay: false });
    };

    /**
     * Save and Continue from the guest/new-user Google Pay L3 expanded view. Commits the
     * GooglePayPaymentGroup via the shared thunk (mirrors PayPal's handlePayPalBraintreeSelect),
     * then collapses to L1 on success. On failure the thunk shows an inline error and the
     * customer stays on the expanded view (PAY-6887 AC3).
     */
    handleGooglePaySaveAndContinue = () => {
        const orderId = OrderUtils.getOrderId();
        const getText = localeUtils.getLocalisedDataFromResource(GooglePayEN, GooglePayFR);
        const errorMessage = getText('googlePayErrorMessage');

        this.props
            .initializeGooglePayPaymentGroup({ orderId, errorMessage })
            .then(() => {
                this.props.commonOrderToggleActions({ googlePay: true });
                this.props.googlePaySetReady(true);
                this.props.clearPendingCreditCard?.();
                this.setState({ shouldAutoExpandGooglePay: false });
                this.closeEditStateWithVendorTracking(anaConsts.ACTION_INFO.VENDOR_GOOGLEPAY);
            })
            .catch(() => {
                // AC3: inline error dispatched inside the thunk; keep the customer on the expanded view.
            });
    };

    handleGiftCardFormVisibilityOnL1 = isVisible => {
        this.setState({ isGiftCardFormOpenOnL1: isVisible });
    };

    /**
     * Handles Apple Pay click from NewUserPaymentView (guest/new user path).
     */
    handlePayWithApplePayClick = () => {
        if (checkoutUtils.isGuestOrder()) {
            ApplePay.enableGuestCheckout();
        }

        ApplePay.onApplePayClicked();
    };

    /**
     * Opens Venmo expanded view from NewUserPaymentView (guest/new user path).
     */
    handleVenmoExpandFromL1 = ({ isExpressPayment = true } = {}) => {
        this.processPaymentClickEvent('venmo', { isExpressPayment });

        Venmo.prepareVenmoCheckout();
        this.setState({ shouldAutoExpandVenmo: true });
    };

    /**
     * Triggered when user clicks "Save and Continue" in Venmo expanded view.
     * Selects Venmo and returns to L1 state.
     */
    handleVenmoSaveAndContinue = () => {
        Venmo.initializeVenmoCheckout();
        this.props.commonOrderToggleActions && this.props.commonOrderToggleActions({ venmo: true });
        this.props.updatePaymentActions && this.props.updatePaymentActions(false, false);
        this.props.clearPendingCreditCard && this.props.clearPendingCreditCard();
        this.setEditChangeLink(false);
        this.setState({ shouldAutoExpandVenmo: false });
        this.closeEditStateWithVendorTracking(anaConsts.ACTION_INFO.VENDOR_VENMO);
    };

    /**
     * Triggered when user clicks "Cancel" in Venmo expanded view.
     * Collapses the L3 expanded view without selecting Venmo.
     * Returns user to L2 edit mode.
     */
    clearAutoExpandVenmo = () => {
        this.setState({ shouldAutoExpandVenmo: false });
    };

    /**
     * NEW flow: selects PayPal via Braintree and returns to L1.
     * Awaits payment group initialization so the UI does not collapse to L1
     * until the order actually has the PayPal payment group attached.
     * Used when the useBraintreePayPalCheckout killswitch is TRUE.
     */
    handlePayPalBraintreeSelect = async () => {
        this.props.paypalToggleSelect?.(true);
        await PayPalCheckout.initializePayPalPaymentGroup();
        this.props.commonOrderToggleActions?.({ paypal: true });
        this.props.updatePaymentActions?.(false, false);
        this.props.clearPendingCreditCard?.();
        this.setEditChangeLink(false);
        this.setState({ shouldAutoExpandPayPal: false });
        this.closeEditStateWithVendorTracking(anaConsts.ACTION_INFO.VENDOR_PAYPAL);
    };

    /**
     * LEGACY flow: finalizes PayPal selection after the popup completes.
     * Runs only on successful popup completion so cancellation keeps the user in L2/L3.
     */
    handlePayPalPopupComplete = async () => {
        this.props.commonOrderToggleActions?.();
        this.props.updatePaymentActions?.(false, false);
        this.props.clearPendingCreditCard?.();
        this.props.sectionSaveOrderAction?.(Location.getLocation().pathname, this);
        this.setEditChangeLink(false);
        this.setState({ shouldAutoExpandPayPal: false });
        this.closeEditStateWithVendorTracking(anaConsts.ACTION_INFO.VENDOR_PAYPAL);
        await refreshCheckout()();
    };

    /**
     * Triggered when user clicks "Save and Continue" in PayPal expanded view (L3).
     * Selects PayPal and returns to L1 state.
     */
    handlePayPalSaveAndContinue = () => {
        const { useBraintreePayPalCheckout } = this.state;

        if (useBraintreePayPalCheckout) {
            this.handlePayPalBraintreeSelect();
        } else {
            PayPal.showPayPal(async payload => {
                try {
                    await this.props.payPalWithInterstice(payload);
                    await this.handlePayPalPopupComplete();
                } catch (err) {
                    // Keep the user in the current edit state when payPalWithInterstice fails, which
                    // probably will never happen
                    Sephora.logger.error(err);
                }
            });
        }
    };

    /**
     * Triggered when user clicks "Cancel" in PayPal expanded view.
     * Collapses the L3 expanded view without selecting PayPal.
     * Returns user to L2 edit mode.
     */
    clearAutoExpandPayPal = () => {
        this.setState({ shouldAutoExpandPayPal: false });
    };

    // Triggered when user clicks "Save and Continue" in Paze expanded view aka L3.
    // Selects Paze and returns to L1 state.

    handlePazeSaveAndContinue = () => {
        this.props.loadPaze && this.props.loadPaze({});
        this.props.commonOrderToggleActions && this.props.commonOrderToggleActions();
        this.props.updatePaymentActions && this.props.updatePaymentActions(true, false);
        this.props.clearPendingCreditCard && this.props.clearPendingCreditCard();
        this.setState({ shouldAutoExpandPaze: false });
        this.closeEditStateWithVendorTracking(anaConsts.ACTION_INFO.VENDOR_PAZE);
    };

    // Collapses back to L2 without selecting Paze.
    handlePazeCancel = () => {
        this.setState({ shouldAutoExpandPaze: false });
    };

    /**
     * Shows the AfterPay expanded view. The actual widget loading is triggered
     * by AfterPayExpandedView after mount (to ensure DOM element exists).
     */
    handleAfterPayExpand = async () => {
        const widgetElementId = 'afterpay-widget-expanded-view';

        // Store current payment state before expanding
        // This allows Cancel to restore previous state
        const {
            alternateMethodSelected, alternatePaymentName, klarna, afterpay, paypal, venmo, googlePay, paze, selectedAsDefaultPaymentName
        } =
            this.props;

        this.setState({
            previousPaymentSelection: {
                alternateMethodSelected,
                alternatePaymentName,
                klarnaSelected: klarna?.isSelected,
                afterpaySelected: afterpay?.isSelected,
                paypalSelected: paypal?.isSelected,
                venmoSelected: venmo?.isSelected,
                googlePaySelected: googlePay?.isSelected,
                pazeSelected: paze?.isSelected,
                selectedAsDefaultPaymentName
            },
            isAfterPayExpanded: true,
            isAfterPayLoading: true,
            afterpayError: null,
            afterpayApiData: { widgetElementId }
        });
    };

    /**
     * Callback for AfterPayExpandedView to load the widget after mount
     * This ensures the DOM element exists before calling the SDK
     */
    loadAfterPayWidget = async () => {
        const { orderDetails, loadExpandedViewWidget } = this.props;
        const priceInfo = orderDetails?.priceInfo;
        // Try multiple fields to get the order amount bc the SDK requires a positive amount
        const orderTotal = priceInfo?.creditCardAmount || priceInfo?.paypalAmount || priceInfo?.orderTotal || 0;
        const widgetElementId = 'afterpay-widget-expanded-view';

        // Afterpay SDK requires a positive amount, show error if amount is 0 or invalid
        if (!orderTotal || orderTotal === '$0.00' || orderTotal === '0') {
            this.setState({
                isAfterPayLoading: false,
                afterpayError: 'Unable to calculate payment schedule. Please try again.'
            });

            return;
        }

        if (loadExpandedViewWidget) {
            // Set a timeout fallback in case SDK never calls onReady
            const timeoutId = setTimeout(() => {
                this.setState({ isAfterPayLoading: false });
            }, 10000); // 10 second timeout

            try {
                await loadExpandedViewWidget(widgetElementId, orderTotal);
                clearTimeout(timeoutId);
                this.setState({ isAfterPayLoading: false });
            } catch (error) {
                clearTimeout(timeoutId);
                this.setState({
                    isAfterPayLoading: false,
                    afterpayError: error?.message || 'Unable to load Afterpay information. Please try again.'
                });
            }
        } else {
            this.setState({ isAfterPayLoading: false });
        }
    };

    /**
     * Collapses L3, selects Afterpay, returns to L1.
     * This is the ONLY place where Afterpay gets selected (user confirmed).
     */
    handleAfterPaySaveAndContinue = () => {
        // Select Afterpay in Redux (user confirmed choice)
        this.props.commonOrderToggleActions && this.props.commonOrderToggleActions({ afterpay: true });
        this.props.afterpaySetReady(true);
        this.props.setPaymentMethodAfterPay(this.props.localization.afterpayErrorMessage);
        this.props.clearPendingCreditCard();

        this.setState({
            isAfterPayExpanded: false,
            afterpayApiData: null,
            previousPaymentSelection: null
        });

        this.closeEditStateWithVendorTracking(anaConsts.ACTION_INFO.VENDOR_AFTERPAY);
    };

    /**
     * Reverts "Set as default payment" if user checked it but then clicked Cancel.
     * Works for any payment method (Afterpay, Klarna, Venmo, PayPal, etc.)
     * @param {string} paymentMethodName - Payment method name (e.g., 'afterpay', 'klarna', 'venmo')
     */
    revertDefaultPaymentIfChanged = paymentMethodName => {
        const currentSelectedAsDefault = this.props.selectedAsDefaultPaymentName;
        const previousSelectedAsDefault = this.state.previousPaymentSelection?.selectedAsDefaultPaymentName;

        if (currentSelectedAsDefault === paymentMethodName && previousSelectedAsDefault !== paymentMethodName) {
            // User set payment method as default but then canceled - remove from backend
            this.props.removeDefaultPaymentFromProfile(paymentMethodName);
        }
    };

    /**
     * Collapses L3 without selecting Afterpay.
     * For new users: returns to L2 (NewUserPaymentView).
     * For existing users: returns to L1 (collapsed state).
     */
    handleAfterPayCancel = () => {
        const hasNoPayments = this.hasNoSavedPaymentMethods();
        const { previousPaymentSelection } = this.state;

        // Revert default payment if user changed it but then canceled
        // We have to call this before clearing previousPaymentSelection state
        this.revertDefaultPaymentIfChanged('afterpay');

        // Restore or clear Afterpay selection based on previous state
        const afterpayWasPreviouslySelected = previousPaymentSelection?.afterpaySelected === true;

        if (afterpayWasPreviouslySelected) {
            this.props.commonOrderToggleActions && this.props.commonOrderToggleActions({ afterpay: true });
            this.props.afterpaySetReady && this.props.afterpaySetReady(true);

            // Now safe to clear UI state, afterpay selection is already restored in Redux
            this.setState({
                isAfterPayExpanded: false,
                afterpayApiData: null,
                afterpayError: null,
                previousPaymentSelection: null
            });

            // Always return to L1, the restored Afterpay is a valid payment
            this.closeEditState();
        } else {
            // Afterpay wasn't selected before, deselect it, preserve other selections
            this.props.afterpaySetReady && this.props.afterpaySetReady(false);
            const klarnaStillSelected = previousPaymentSelection?.klarnaSelected || false;
            const paypalStillSelected = previousPaymentSelection?.paypalSelected || false;
            const venmoStillSelected = previousPaymentSelection?.venmoSelected || false;
            const googlePayStillSelected = previousPaymentSelection?.googlePaySelected || false;
            const pazeStillSelected = previousPaymentSelection?.pazeSelected || false;

            // Restore the previously selected payment method
            this.props.commonOrderToggleActions &&
                this.props.commonOrderToggleActions({
                    afterpay: false,
                    klarna: klarnaStillSelected,
                    paypal: paypalStillSelected,
                    venmo: venmoStillSelected,
                    googlePay: googlePayStillSelected,
                    paze: pazeStillSelected
                });

            // Clear UI state
            this.setState({
                isAfterPayExpanded: false,
                afterpayApiData: null,
                afterpayError: null,
                previousPaymentSelection: null
            });

            // Navigation logic: new users stay in L2, existing users return to L1
            if (!hasNoPayments) {
                this.closeEditState();
            }
        }
    };

    /**
     * Callback from PaymentMethodList to clear the auto-expand flag
     * after AfterPay expansion has been triggered.
     */
    clearAutoExpandAfterpay = () => {
        this.setState({ shouldAutoExpandAfterpay: false });
    };

    /**
     * Opens Payment edit view (L2) then triggers Klarna L3 expansion.
     * Called when user clicks Klarna pill from L1 state.
     */
    handleKlarnaExpandFromL1 = ({ isExpressPayment = true } = {}) => {
        this.processPaymentClickEvent('klarna', { isExpressPayment });

        this.setEditChangeLink(true, true);
        this.handleKlarnaExpand();
    };

    /**
     * Shows Klarna expanded view (L3 state).
     * Widget loading happens after mount via KlarnaExpandedView.
     */
    handleKlarnaExpand = async () => {
        const widgetElementId = 'klarna-widget-expanded-view';

        // Store current payment state before expanding
        // This allows Cancel to restore previous state
        const {
            alternateMethodSelected,
            alternatePaymentName,
            klarna,
            afterpay,
            paypal,
            venmo,
            googlePay,
            paze,
            selectedAsDefaultPaymentName,
            creditCardPaymentGroup
        } = this.props;

        // Snapshot CC id BEFORE loadKlarnaWidget mutates orderDetails server-side,
        // so handleKlarnaCancel can re-attach the CC if the user cancels.
        const previousCreditCardId = creditCardPaymentGroup?.creditCardId || null;

        this.setState({
            previousPaymentSelection: {
                alternateMethodSelected,
                alternatePaymentName,
                klarnaSelected: klarna?.isSelected,
                afterpaySelected: afterpay?.isSelected,
                paypalSelected: paypal?.isSelected,
                venmoSelected: venmo?.isSelected,
                googlePaySelected: googlePay?.isSelected,
                pazeSelected: paze?.isSelected,
                selectedAsDefaultPaymentName,
                previousCreditCardId
            },
            isKlarnaExpanded: true,
            isKlarnaLoading: true,
            klarnaError: null,
            klarnaApiData: { widgetElementId }
        });
    };

    /**
     * Loads Klarna SDK widget after DOM element is ready.
     * Uses shared Util to keep code dry.
     *
     * The returned promise is captured on the instance so handleKlarnaCancel can
     * await it before restoring the CC — otherwise the in-flight createSession
     * response would land after cancel and clobber the restored order state.
     */
    loadKlarnaWidget = async () => {
        const loader = createKlarnaWidgetLoader({
            widgetElementId: 'klarna-widget-expanded-view',
            setState: this.setState.bind(this),
            klarnaSetReady: this.props.klarnaSetReady,
            persistKlarnaEmd: this.props.persistKlarnaEmd,
            locales: this.props.localization
        });

        this.klarnaInitPromise = loader();

        return this.klarnaInitPromise;
    };

    /**
     * Collapses L3, selects Klarna, returns to L1.
     * This is the ONLY place where Klarna gets selected (user confirmed).
     */
    handleKlarnaSaveAndContinue = () => {
        //  Select Klarna in Redux (user confirmed choice)
        this.props.commonOrderToggleActions && this.props.commonOrderToggleActions({ klarna: true });
        this.props.klarnaSetReady && this.props.klarnaSetReady(true);
        this.props.clearPendingCreditCard();

        this.klarnaInitPromise = null;

        this.setState({
            isKlarnaExpanded: false,
            klarnaApiData: null,
            previousPaymentSelection: null
        });

        this.closeEditStateWithVendorTracking(anaConsts.ACTION_INFO.VENDOR_KLARNA);
    };

    /**
     * Collapses L3 without selecting Klarna.
     * For new users: returns to L2 (NewUserPaymentView).
     * For existing users: returns to L1 (collapsed state).
     */
    handleKlarnaCancel = async () => {
        const hasNoPayments = this.hasNoSavedPaymentMethods();
        const { previousPaymentSelection } = this.state;

        // Revert default payment if user changed it but then canceled
        this.revertDefaultPaymentIfChanged('klarna');

        // Restore or clear Klarna selection based on previous state
        const klarnaWasPreviouslySelected = previousPaymentSelection?.klarnaSelected === true;

        if (klarnaWasPreviouslySelected) {
            // Klarna was the saved payment, restore it since user canceled.
            this.props.commonOrderToggleActions && this.props.commonOrderToggleActions({ klarna: true });
            this.props.klarnaSetReady && this.props.klarnaSetReady(true);

            // Now safe to clear UI state — Klarna selection is already restored in Redux
            this.setState({
                isKlarnaExpanded: false,
                klarnaApiData: null,
                klarnaError: null,
                previousPaymentSelection: null
            });

            // Always return to L1, the restored Klarna is a valid payment
            this.closeEditState();
        } else {
            // Klarna wasn't the saved payment — deselect it and restore whatever was selected before.
            this.props.klarnaSetReady && this.props.klarnaSetReady(false);

            // Restore each method's Redux isSelected flag. commonOrderToggleActions resets any omitted
            // method to false, so every preserved method must be passed explicitly.
            this.props.commonOrderToggleActions &&
                this.props.commonOrderToggleActions({
                    klarna: false,
                    afterpay: previousPaymentSelection?.afterpaySelected || false,
                    paypal: previousPaymentSelection?.paypalSelected || false,
                    venmo: previousPaymentSelection?.venmoSelected || false,
                    googlePay: previousPaymentSelection?.googlePaySelected || false,
                    paze: previousPaymentSelection?.pazeSelected || false
                });

            // Wait for any in-flight Klarna init so its late response can't clobber the restore below.
            // createSession replaces whatever payment group was on the order with a Klarna group; if
            // cancel runs before that lands, we'd race it. Await guarantees we restore last.
            if (this.klarnaInitPromise) {
                try {
                    await this.klarnaInitPromise;
                } catch (e) {
                    // Swallow: the in-flight createSession may have failed; we restore regardless.
                }
                this.klarnaInitPromise = null;
            }

            // Restoring credit card attached to order before Klarna was initialized and canceled
            await this.reattachCreditCardAfterKlarnaCancel(previousPaymentSelection);

            // Clear UI state
            this.setState({
                isKlarnaExpanded: false,
                klarnaApiData: null,
                klarnaError: null,
                previousPaymentSelection: null
            });

            // Navigate based on saved payments
            if (!hasNoPayments) {
                this.closeEditState();
            }
        }
    };

    /**
     * Re-attaches the credit card that was on the order before the user opened the Klarna pill.
     *
     * Expanding Klarna calls initializeKlarnaCheckout (otherwise, Klarna can't show the widget), which
     *  replaces the order's current payment group with a Klarna group. On cancel, restoring the Redux
     * isSelected flag is enough for alternative payment methods: each one's Place Order button
     * re-creates its payment group at submit time, BUT NOT SAVED CREDIT CARDS.
     *
     * @param {Object} previousPaymentSelection - snapshot taken in handleKlarnaExpand
     */
    reattachCreditCardAfterKlarnaCancel = async previousPaymentSelection => {
        const { previousCreditCardId } = previousPaymentSelection || {};

        if (!previousCreditCardId) {
            return;
        }

        try {
            // Re-attach the CC that was on the order before Klarna init removed it.
            const cardToRestore = (this.props.paymentOptions?.creditCards || []).find(card => card.creditCardId === previousCreditCardId);

            if (cardToRestore) {
                await this.props.reattachCreditCardToOrder(cardToRestore);
            }
        } catch (e) {
            Sephora.logger.error('Klarna cancel: failed to re-attach credit card on order', e);
        }
    };

    /**
     * Callback from PaymentMethodList to clear the auto-expand flag
     * after Klarna expansion has been triggered.
     */
    clearAutoExpandKlarna = () => {
        this.setState({ shouldAutoExpandKlarna: false });
    };

    /**
     * Clears PayPal auto-expand flag after PaymentMethodList consumes it.
     */
    clearAutoExpandPayPal = () => {
        this.setState({ shouldAutoExpandPayPal: false });
    };

    /**
     * Clears Paze auto-expand flag after PaymentMethodList consumes it.
     */
    clearAutoExpandPaze = () => {
        this.setState({ shouldAutoExpandPaze: false });
    };

    /**
     * Toggles billing address checkbox for Klarna
     */
    handleBillingAddressToggle = () => {
        this.setState(prevState => ({
            useMyShippingAddress: !prevState.useMyShippingAddress
        }));
    };

    componentDidMount() {
        const { orderDetails, tmpCardMessage, gcAmountNotEnoughMessage, alternateMethodSelected } = this.props;

        // Don’t sync Redux with backend if the user already picked an alternate payment.
        // Prevents overwriting Klarna/Afterpay selections due to stale orderDetails.
        if (!alternateMethodSelected) {
            this.props.checkSelectedPayment({ orderDetails });
        }

        if (tmpCardMessage) {
            this.loadTempCardMessageModal(tmpCardMessage);
        }

        if (gcAmountNotEnoughMessage) {
            this.setGiftCardAmountNotEnoughMessage();
        }

        if (Sephora.isAgent && agentAwareUtils.isAgentAwareSession()) {
            //If it is Sephora Mirror,for certain roles agent should see payment section open and cvv box opened.

            if (!OrderUtils.isZeroCheckout() && !agentAwareUtils.isAgentAuthorized(['3'])) {
                const updatedOrderDetails = {
                    ...this.props.orderDetails
                };

                // Find and update CreditCardPaymentGroup's isComplete to false
                if (updatedOrderDetails.paymentGroups && Array.isArray(updatedOrderDetails.paymentGroups.paymentGroupsEntries)) {
                    updatedOrderDetails.paymentGroups.paymentGroupsEntries = updatedOrderDetails.paymentGroups.paymentGroupsEntries.map(entry => {
                        if (entry.paymentGroupType === 'CreditCardPaymentGroup' && entry.paymentGroup) {
                            return {
                                ...entry,
                                paymentGroup: {
                                    ...entry.paymentGroup,
                                    isComplete: false
                                }
                            };
                        }

                        return entry;
                    });
                }

                this.props.dispatchOrderUpdate(updatedOrderDetails);
                this.setEditChangeLink(true);
            }
        }

        // For $0 checkout with CVV validation, check if the card actually requires CVV
        // This handles the case when user updated their card in My Account before checkout
        if (FrictionlessUtils.isCVVMissing(orderDetails.header.paymentGroups) || FrictionlessUtils.isCVVMissingForBopis(orderDetails)) {
            this.savePendingCreditCard(orderDetails);
        } else if (OrderUtils.isZeroDollarOrderWithCVVValidation()) {
            this.savePendingCreditCardIfCVVRequired(orderDetails);
        }

        // Check PayPal Braintree Checkout killswitch
        const useBraintreePayPalCheckout = PayPalCheckout.isPayPalCheckoutEnabledForLocale();
        this.setState({ useBraintreePayPalCheckout });
    }

    savePendingCreditCard = orderDetails => {
        const creditCardPaymentGroup = OrderUtils.getCreditCardPaymentGroup(orderDetails) || { isComplete: false };
        const creditCardToSave = this.props.paymentOptions.creditCards.filter(
            creditCard => creditCard.creditCardId === creditCardPaymentGroup?.creditCardId
        )?.[0];
        // SET PENDING CC
        this.props.savePendingCreditCard(creditCardToSave);
    };

    savePendingCreditCardIfCVVRequired = orderDetails => {
        const creditCards = this.props.paymentOptions?.creditCards || [];
        const creditCardPaymentGroup = OrderUtils.getCreditCardPaymentGroup(orderDetails) || { isComplete: false };
        const creditCardToSave = creditCards.filter(creditCard => creditCard.creditCardId === creditCardPaymentGroup?.creditCardId)?.[0];

        // Only set as pending if CVV modal should be shown for this card
        // This prevents setting pendingCreditCard for pre-approved cards
        if (creditCardToSave && FrictionlessUtils.showCVVModal(creditCardToSave, orderDetails)) {
            this.props.savePendingCreditCard(creditCardToSave);
        }
    };

    setGiftCardAmountNotEnoughMessage = () => {
        this.props.setCheckoutSectionErrors({ Payment: this.props.gcAmountNotEnoughMessage });
    };

    loadTempCardMessageModal = tmpCardMessage => {
        this.props.showInfoModal({
            isOpen: true,
            title: StringUtils.capitalize(tmpCardMessage.type),
            message: tmpCardMessage.messages.join('. ')
        });
    };

    //TODO: will be updated in next stories
    handleAlternatePaymentDetails = () => {
        const { alternatePaymentDetailsModal: { sid, title, width } = Empty.Object, showContentModal, localization } = this.props;

        showContentModal({
            isOpen: true,
            data: {
                sid,
                title,
                width,
                showCloseButton: true,
                closeButtonText: localization.gotIt,
                hasBorderForFooteronLGUI: false,
                hasShadowBorderForSMUI: true,
                isDrawer: true,
                minHeightCloseBtn: 44
            }
        });
    };

    closeEditState = () => {
        this.exitingViaCloseEditState = true;
        this.userInitiatedEditMode = false;
        this.setState({
            isHigherRedeemPoints: false
        });
        this.props.goToURL({ path: FC_CHECKOUT_PATH });
        this.props.setPaymentMethodNeedsConfirmation(false);
    };

    handleSuccessfulPaymentUpdate = () => {
        this.props.clearNamedSectionErrors([SECTION_NAMES.PAYMENT]);

        // SHP-4913: Any successful exit from the payment edit views (CVV entered + Use This Card
        // on L3, Use This Card on L2 without changes, or an alternate Save & Continue) clears
        // the payment-verification-failure request flag so the red banner does not reappear.
        if (this.props.paymentVerificationFailureRequested) {
            this.props.setPaymentVerificationFailureRequested(false);
        }

        this.closeEditState();
    };

    /**
     * SHP-4913: When the user dismisses the payment-verification-failure modal, the
     * `paymentVerificationFailureRequested` flag flips true. Open L2 here so PaymentMethodList
     * can pick up the flag and auto-open L3 for the in-order credit card.
     */
    handlePaymentVerificationFailureRequest = prevProps => {
        const justRequested = !prevProps.paymentVerificationFailureRequested && this.props.paymentVerificationFailureRequested;

        if (justRequested && !this.props.isEditViewFromURL) {
            this.setEditChangeLink(true, true);
        }
    };

    processVendorSaveAndContinueEvent = vendor => {
        const normalizedVendor = vendor.toLowerCase();
        const selectedDefault = this.props.selectedAsDefaultPaymentName?.toLowerCase();
        const isDefaultPayment = !!selectedDefault && selectedDefault === normalizedVendor;
        const isGooglePay = normalizedVendor === anaConsts.ACTION_INFO.VENDOR_GOOGLEPAY;
        FrictionlessCheckoutBindings.trackPaymentVendorInteraction(`${anaConsts.ACTION_INFO.SAVE_AND_CONTINUE}:${vendor}`, {
            isDefaultPayment,
            paymentMethod: isGooglePay ? normalizedVendor : undefined
        });
    };

    closeEditStateWithVendorTracking = vendor => {
        this.processVendorSaveAndContinueEvent(vendor);
        this.handleSuccessfulPaymentUpdate();
    };

    shouldOpenPaymentSection = () => {
        const {
            alternateMethodSelected,
            isPaypalSelected,
            payPalPaymentGroup,
            giftCardPaymentGroups,
            creditCardPaymentGroup,
            isPaymentComplete,
            isShippableOrder,
            hasSavedAddress,
            forcePaymentSectionRender,
            isEditViewFromURL,
            tmpCardMessage
        } = this.props;
        const { suppressAutoOpenAfterExternalExit } = this.state;

        const isZeroDollarCVVDeferred = this.props.zeroDollarHigherRedeemPointsCVV;

        const hasNoPaymentMethod =
            !suppressAutoOpenAfterExternalExit &&
            !alternateMethodSelected &&
            !isEditViewFromURL &&
            !isPaypalSelected &&
            !payPalPaymentGroup?.isComplete &&
            !giftCardPaymentGroups.length > 0 &&
            (!creditCardPaymentGroup || !creditCardPaymentGroup.isComplete) &&
            !isPaymentComplete &&
            !isZeroDollarCVVDeferred &&
            (!isShippableOrder || hasSavedAddress);

        const shouldOpenForRedeemPoints = !isEditViewFromURL && this.state.isHigherRedeemPoints && !forcePaymentSectionRender;
        const shouldOpenForTempCardMessage = !isEditViewFromURL && Boolean(tmpCardMessage);

        return hasNoPaymentMethod || shouldOpenForRedeemPoints || shouldOpenForTempCardMessage;
    };

    shouldCollapsePaymentSection = () => {
        const {
            isEditViewFromURL, alternateMethodSelected, isPaypalSelected, hasSavedAddress, isNewUserFlow, hasNoPaymentOptionSelected
        } =
            this.props;
        // Keep expanded when an alternate payment (Klarna/Afterpay/Venmo/Paze) OR PayPal is selected AND user has address.
        // PayPal is tracked separately from alternateMethodSelected, so it must be checked explicitly to avoid collapsing
        // the section after Save & Continue in the new Braintree flow (where payPalPaymentGroup.isComplete is still false).
        const shouldShowSelectedPayment = (alternateMethodSelected || isPaypalSelected) && hasSavedAddress;
        const isZeroDollarCVVDeferred = this.props.zeroDollarHigherRedeemPointsCVV;

        // Collapse payment section when user has no saved payment method to display
        // Don't collapse for $0 CVV orders: show L1 with default card, CVV deferred to Place Order
        if (isZeroDollarCVVDeferred) {
            return false;
        }

        // SHP-4641: Don't collapse when gift card covers the full order.
        // Show the applied GC summary on L1 instead of hiding the section.
        if (!isEditViewFromURL && this.props.giftCardAmountEqualsOrderTotal && this.props.giftCardPaymentGroups?.length > 0) {
            return false;
        }

        // SHP-4658: Don't collapse when account credit covers the full order.
        // Show the applied account credit summary on L1 instead of hiding the section.
        if (!isEditViewFromURL && this.isAccountCreditCoveringFullOrder()) {
            return false;
        }

        if (!isEditViewFromURL && this.shouldShowNewUserPaymentView()) {
            return true;
        }

        if (!isEditViewFromURL && !hasSavedAddress && (isNewUserFlow || hasNoPaymentOptionSelected)) {
            return true;
        }

        return !isEditViewFromURL && !this.shouldOpenPaymentSection() && !shouldShowSelectedPayment && (isNewUserFlow || hasNoPaymentOptionSelected);
    };

    // SHP-3342: Close payment section on mWeb when PayPal completes.
    // mWeb uses URL-based routing (isEditViewFromURL) instead of component state.
    // Detect PayPal incomplete → complete and navigate away from /payment to collapse the section.
    shouldClosePaymentSection = prevProps => {
        const { payPalPaymentGroup, isEditViewFromURL } = this.props;

        return (
            isEditViewFromURL &&
            !prevProps.payPalPaymentGroup?.isComplete && // PayPal was incomplete before
            payPalPaymentGroup?.isComplete // PayPal is now complete (user returned from PayPal auth)
        );
    };

    /**
     * Helper to check if user has no saved payment methods.
     * Used to determine navigation behavior on Cancel (L2 vs L1).
     */
    hasNoSavedPaymentMethods = () => {
        const { creditCardPaymentGroup, payPalPaymentGroup, paymentOptions } = this.props;

        const noCreditCards = !paymentOptions?.creditCards?.length;
        const noPayPal = !payPalPaymentGroup?.isComplete;
        const creditCardIncomplete = !creditCardPaymentGroup?.isComplete;

        return noCreditCards && noPayPal && creditCardIncomplete;
    };

    /**
     * Detects if account credit fully covers the order total.
     * When true, no additional payment is needed (order total is $0.00 after account credit).
     * This is implicit detection based on priceInfo values:
     * - storeCardAmount exists (account credit is applied)
     * - Both paypalAmount and creditCardAmount are '$0.00' or falsy
     */
    isAccountCreditCoveringFullOrder = () => {
        const { orderDetails } = this.props;
        const priceInfo = orderDetails?.priceInfo;

        if (!priceInfo?.storeCardAmount) {
            return false;
        }

        const paypalAmount = priceInfo.paypalAmount;
        const creditCardAmount = priceInfo.creditCardAmount;

        // If both payment amounts are $0.00 or falsy, account credit covers everything
        return (!paypalAmount || paypalAmount === '$0.00') && (!creditCardAmount || creditCardAmount === '$0.00');
    };

    /**
     * Detects if account credit is applied to the order total.
     * This doesn't necessarily mean the order total is $0.00, just that account credit is being used.
     */
    isStoreCreditApplied = () => {
        const { orderDetails } = this.props;
        const priceInfo = orderDetails?.priceInfo;

        if (!priceInfo?.storeCardAmount) {
            return false;
        }

        return Boolean(priceInfo?.storeCardAmount);
    };

    /**
     * SHP-2591: Determines if we should show the new user payment view
     * Shows when user has no saved payment method AND has a saved address
     * Applies to: SaD checkout, BOPIS checkout, Guest checkout
     */
    shouldShowNewUserPaymentView = () => {
        const {
            creditCardPaymentGroup, payPalPaymentGroup, hasSavedAddress, paymentOptions, orderDetails, pendingCreditCard
        } = this.props;
        const { openCreditCardForm } = this.state;

        // Check if user has no saved credit cards
        const hasNoSavedCreditCards = !paymentOptions?.creditCards || paymentOptions.creditCards.length === 0;

        // Check if user has no saved PayPal (not just available, but actually saved and complete)
        const hasNoSavedPayPal = !payPalPaymentGroup?.isComplete;

        // Check if credit card payment group is not complete
        const creditCardNotComplete = !creditCardPaymentGroup?.isComplete;

        // User has no saved payment method if all of the above are true
        const hasNoSavedPaymentMethod = hasNoSavedCreditCards && hasNoSavedPayPal && creditCardNotComplete;

        const hasNoAlternatePaymentSaved =
            FrictionlessUtils.getSavedAlternatePayments(paymentOptions, orderDetails, Boolean(pendingCreditCard))?.length === 0;

        // Show new user view when: no payment method AND has saved address AND no alternate payment selected
        return (
            hasNoSavedPaymentMethod &&
            hasSavedAddress &&
            !openCreditCardForm &&
            !this.state.shouldAutoExpandPayPal &&
            !this.props.alternateMethodSelected &&
            hasNoAlternatePaymentSaved
        );
    };

    handlePaymentSectionOpenLogic = () => {
        const { activeSection } = this.props;
        const shouldOpen = this.shouldOpenPaymentSection();

        if (shouldOpen && activeSection !== SECTION_NAMES.PAYMENT) {
            this.props.setActiveSection(SECTION_NAMES.PAYMENT);
            FrictionlessCheckoutBindings.resetPaymentButtonStateTracking();
            this.setAnalytics();
        }
    };

    handlePaymentCheckOnExitEditView = prevProps => {
        const {
            isEditViewFromURL, orderDetails, availablePaymentMethods, isAlternatePaymentMethodSelected, alternateMethodSelected
        } = this.props;

        const isExitingEditMode = !isEditViewFromURL && prevProps.isEditViewFromURL;

        // Only act on the actual edit-mode exit transition, not intermediate renders
        if (!isExitingEditMode) {
            return;
        }

        // Skip when exiting via closeEditState (save/cancel handlers already manage state).
        // This prevents a race condition where this.props.alternateMethodSelected is stale
        if (this.exitingViaCloseEditState) {
            this.exitingViaCloseEditState = false;

            return;
        }

        const shouldCheckPayment = isAlternatePaymentMethodSelected(orderDetails, availablePaymentMethods) && !alternateMethodSelected;
        const shouldSuppressAutoOpen = this.hasNoSavedPaymentMethods() && !alternateMethodSelected;

        const nextState = {};

        if (shouldSuppressAutoOpen && !this.state.suppressAutoOpenAfterExternalExit) {
            nextState.suppressAutoOpenAfterExternalExit = true;
        }

        if (this.state.openCreditCardForm) {
            nextState.openCreditCardForm = false;
        }

        if (Object.keys(nextState).length > 0) {
            this.setState(nextState);
        }

        if (shouldCheckPayment) {
            this.props.checkSelectedPayment({ orderDetails });
        }
    };

    handleEnterEditModeLogic = prevProps => {
        const { isEditViewFromURL, alternateMethodSelected, alternatePaymentName, hasSavedOrDefaultAlternatePayment } = this.props;
        const isEnteringEditMode = !prevProps.isEditViewFromURL && isEditViewFromURL;
        const isVenmoExpressSavedState = Venmo.isVenmoExpressFlow() && alternatePaymentName === PAYMENT_TYPES.PAY_VENMO;

        if (!isEnteringEditMode) {
            return;
        }

        if (isVenmoExpressSavedState && !this.didHandleVenmoExpressAutoClose) {
            this.didHandleVenmoExpressAutoClose = true;

            // Auto-close only for Venmo express saved landing.
            // Keep normal Change behavior for other alternate methods.
            this.closeEditState();

            return;
        }

        // Afterpay's group is never a saved/active alternate (it completes only at place order), so keep
        // its gift-card selection through "Change" like any saved/default alternate.
        const keepSelectedAlternate =
            hasSavedOrDefaultAlternatePayment || FrictionlessUtils.isAfterpayGiftCardPurchaseSelected(this.props.orderDetails);

        // If an alt payment (Klarna/Afterpay/etc.) is selected, edit mode can be:
        // 1) User clicked “Change” then allow edit mode, but reset isReady so CTA goes back to “Place Order”.
        // 2) Auto after address save then backend doesn’t know the Redux-only selection yet, so stay on L1.
        if (alternateMethodSelected && !keepSelectedAlternate) {
            if (this.userInitiatedEditMode) {
                this.userInitiatedEditMode = false;
                this.props.commonOrderToggleActions?.();
                this.props.klarnaSetReady?.(false);
                this.props.afterpaySetReady?.(false);

                return;
            }

            this.closeEditState();

            return;
        }

        if (!keepSelectedAlternate) {
            this.props.commonOrderToggleActions?.();
            this.props.klarnaSetReady?.(false);
            this.props.afterpaySetReady?.(false);
        }
    };

    handleNewUserFlowChange = prevProps => {
        const {
            isEditViewFromURL, isNewUserFlow, tmpCardMessage, orderDetails, activeSection
        } = this.props;

        if (isEditViewFromURL && prevProps.isNewUserFlow && !isNewUserFlow) {
            this.setAnalytics();
        }

        if (prevProps.orderDetails !== orderDetails && tmpCardMessage && activeSection === SECTION_NAMES.PAYMENT) {
            this.loadTempCardMessageModal(tmpCardMessage);
        }
    };

    handlePaymentNeedsConfirmationState = prevProps => {
        const { isEditViewFromURL, paymentSectionNeedsConfirmation } = this.props;

        if (isEditViewFromURL !== prevProps.isEditViewFromURL && paymentSectionNeedsConfirmation !== isEditViewFromURL) {
            this.props.setPaymentMethodNeedsConfirmation(isEditViewFromURL);
        }
    };

    checkForCVVError = prevProps => {
        const { orderDetails, pendingCreditCard } = this.props;

        const havePaymentGroupsChanged = !deepEqual(prevProps.orderDetails.header.paymentGroups, orderDetails.header.paymentGroups);
        const havePaymentGroupEntriesChanged = !deepEqual(
            prevProps.orderDetails.paymentGroups?.paymentGroupsEntries,
            orderDetails.paymentGroups?.paymentGroupsEntries
        );
        const isZeroDollarCVVOrder = OrderUtils.isZeroDollarOrderWithCVVValidation();

        // Set pendingCreditCard if CVV is missing in header.paymentGroups, or for BOPIS orders
        // where the CC paymentGroup is incomplete (BOPIS does not include securityCodeMissingMsg)
        if (
            (havePaymentGroupsChanged && FrictionlessUtils.isCVVMissing(orderDetails.header.paymentGroups)) ||
            (havePaymentGroupEntriesChanged && FrictionlessUtils.isCVVMissingForBopis(orderDetails))
        ) {
            this.savePendingCreditCard(orderDetails);
        } else if (isZeroDollarCVVOrder && !pendingCreditCard) {
            // For $0 CVV orders without pendingCreditCard, check if CVV is actually required
            this.savePendingCreditCardIfCVVRequired(orderDetails);
        }
    };

    updatePendingCreditCard = () => {
        const { pendingCreditCard, orderDetails } = this.props;

        if (!pendingCreditCard) {
            return;
        }

        const creditCardFromPaymentOptions = this.props.paymentOptions?.creditCards?.filter(
            cc => cc.creditCardId === pendingCreditCard?.creditCardId
        )?.[0];

        if (!deepEqual(pendingCreditCard, creditCardFromPaymentOptions)) {
            this.savePendingCreditCard(orderDetails);
        }
    };

    detectGiftCardRemoval = prevProps => {
        const prevCount = prevProps.giftCardPaymentGroups?.length || 0;
        const currentCount = this.props.giftCardPaymentGroups?.length || 0;

        if (currentCount < prevCount) {
            this.props.getCreditCards(this.props.orderId);
            const hasNoDefaultPaymentOnFile = this.hasNoSavedPaymentMethods() && !this.props.hasSavedOrDefaultAlternatePayment;
            // When a gift card that was covering the order in full is removed, open the full payment
            // list so the customer can choose how to pay, instead of collapsing to the summary view.
            const giftCardNoLongerCoversOrder = prevProps.giftCardAmountEqualsOrderTotal && !this.props.giftCardAmountEqualsOrderTotal;

            if (hasNoDefaultPaymentOnFile || giftCardNoLongerCoversOrder) {
                this.setEditChangeLink(true);
            }
        }
    };

    componentDidUpdate(prevProps) {
        this.handlePaymentSectionOpenLogic();
        this.handlePaymentCheckOnExitEditView(prevProps);
        this.handleEnterEditModeLogic(prevProps);

        if (this.shouldClosePaymentSection(prevProps)) {
            this.closeEditState();
        }

        this.handleNewUserFlowChange(prevProps);
        this.checkSelectedPayment(prevProps);
        this.validateGiftCardAmountNotEnoughMessage(prevProps);
        this.handlePaymentNeedsConfirmationState(prevProps);
        this.checkForCVVError(prevProps);
        this.updatePendingCreditCard();
        this.detectGiftCardRemoval(prevProps);
        this.handlePaymentVerificationFailureRequest(prevProps);
    }

    checkSelectedPayment(prevProps) {
        const {
            isNewUserFlow, alternatePaymentName, hasSavedAddress, orderDetails, alternateMethodSelected
        } = this.props;

        if (!this.isNewUserWithVenmoExpress) {
            this.isNewUserWithVenmoExpress = !!(isNewUserFlow && alternatePaymentName === PAYMENT_TYPES.PAY_VENMO && Venmo.isVenmoExpressFlow());
        }

        const orderChanged = prevProps.orderDetails !== orderDetails;
        const justGotAddress = !prevProps.hasSavedAddress && hasSavedAddress;

        // Skip if alternate payment already selected locally—backend won't reflect it yet
        if (orderChanged && (this.isNewUserWithVenmoExpress || justGotAddress) && !alternateMethodSelected) {
            this.props.checkSelectedPayment({ orderDetails });
        }
    }

    setAnalytics = () => {
        const { zeroDollarHigherRedeemPointsNotCVV, paymentOptions, isNewUserFlow } = this.props;
        const openCreditCardForm = zeroDollarHigherRedeemPointsNotCVV && !paymentOptions?.creditCards?.length;
        const pageDetail = openCreditCardForm ? anaConsts.PAGE_NAMES.ADD_CREDIT_CARD : anaConsts.PAGE_TYPES.PAYMENT;

        if (isNewUserFlow) {
            return;
        }

        // NewUserPaymentView fires its own button-state ASYNC_PAGE_LOAD (with eVar140).
        // Skip the generic call so it doesn't produce a duplicate bare pageName.
        if (this.shouldShowNewUserPaymentView()) {
            return;
        }

        // When the user has no saved payment methods, `PaymentMethodList` will fire the more specific
        // button-state ASYNC_PAGE_LOAD (e.g. "...button=add new card"). Skip the generic payment ASYNC_PAGE_LOAD
        // to avoid emitting an extra, less-informative pageName.
        const hasSavedCreditCards = (paymentOptions?.creditCards?.length || 0) > 0;
        const hasSavedAlternatePayments = FrictionlessUtils.hasSavedOrDefaultAlternatePayment(this.props.paymentOptions, this.props.orderDetails);
        const willTrackButtonState = pageDetail === anaConsts.PAGE_TYPES.PAYMENT && !hasSavedCreditCards && !hasSavedAlternatePayments;

        if (willTrackButtonState) {
            return;
        }

        FrictionlessCheckoutBindings.setChangeLinkAnalytics(pageDetail);
    };

    handleGiftCardErrorMessages = prevProps => {
        const { gcAmountNotEnoughMessage, alternateMethodSelected, orderDetails } = this.props;
        const orderChanged = prevProps.orderDetails !== orderDetails;

        if (orderChanged && gcAmountNotEnoughMessage) {
            this.setGiftCardAmountNotEnoughMessage();
        }

        // Clear Payment section error when the gift card amount warning is removed after order refresh.
        if (orderChanged && prevProps.gcAmountNotEnoughMessage && !gcAmountNotEnoughMessage) {
            this.props.clearNamedSectionErrors([SECTION_NAMES.PAYMENT]);
        }

        if (orderChanged && prevProps.gcAmountNotEnoughMessage && alternateMethodSelected) {
            this.props.clearCheckoutSectionErrors();
        }
    };

    // When a gift card transitions to covering the order in full, any previously selected
    // payment must be removed so it is not used at Place Order. Alternate methods (PayPal/Venmo/
    // Klarna/Afterpay/Paze/Google Pay) are Redux-only selections cleared via commonOrderToggleActions;
    // a saved credit card is attached to the order and must be detached via removeOrderPayment.
    detachPreviousPaymentOnFullGiftCardCover = () => {
        const {
            alternatePaymentName, isPaypalSelected, creditCardPaymentGroup, pendingCreditCard, orderId
        } = this.props;

        if (alternatePaymentName || isPaypalSelected) {
            this.props.commonOrderToggleActions();
        }

        if (pendingCreditCard) {
            this.props.clearPendingCreditCard();
        }

        const creditCardGroupId = creditCardPaymentGroup?.paymentGroupId;

        if (!creditCardGroupId) {
            return;
        }

        const creditCardId = creditCardUtils.isSavedToProfile(creditCardPaymentGroup.creditCardId) ? creditCardPaymentGroup.creditCardId : null;

        this.props
            .removeOrderPayment(orderId, creditCardGroupId, creditCardId)
            .then(() => {
                this.props.getCreditCards(orderId);

                return this.props.getOrderDetails(orderId).then(newOrderDetails => this.props.dispatchOrderUpdate(newOrderDetails));
            })
            .catch(errorData => ErrorsUtils.collectAndValidateBackEndErrors(errorData, this));
    };

    validateGiftCardAmountNotEnoughMessage = prevProps => {
        const {
            orderDetails, giftCardAmountEqualsOrderTotal, isEditViewFromURL, isSubscriptionOrder, hasNoSavedCreditCards
        } = this.props;

        this.handleGiftCardErrorMessages(prevProps);

        // Subscription orders always require a payment method on file for recurring charges,
        // so we never detach the prior payment even when a gift card covers the current order.
        if (prevProps.giftCardAmountEqualsOrderTotal !== giftCardAmountEqualsOrderTotal && giftCardAmountEqualsOrderTotal && !isSubscriptionOrder) {
            this.detachPreviousPaymentOnFullGiftCardCover();
        }

        const shouldStayInEditMode = isSubscriptionOrder && hasNoSavedCreditCards;
        const gcNowCoversOrder = prevProps.giftCardAmountEqualsOrderTotal !== giftCardAmountEqualsOrderTotal && giftCardAmountEqualsOrderTotal;
        const orderChanged = prevProps.orderDetails !== orderDetails;
        const orderChangedAndACCoversOrder = orderChanged && this.isAccountCreditCoveringFullOrder();

        if ((gcNowCoversOrder || orderChangedAndACCoversOrder) && isEditViewFromURL && !shouldStayInEditMode) {
            this.closeEditState();
        }
    };

    renderNewUserView = (isFullyCoveredByAccountCredit, isStoreCreditApplied, newUserAvailablePaymentMethods) => {
        const {
            localization,
            giftCardPaymentGroups,
            isGuestCheckout,
            paymentOptions,
            storeCredits,
            isPaymentComplete,
            giftCardAmountEqualsOrderTotal,
            isSubscriptionOrder,
            hasNoSavedCreditCards,
            sectionLevelError,
            gcAmountNotEnoughMessage,
            physicalGiftCardShippingGroup,
            shouldDisplayAutoReplenMessage,
            shouldDisplaySDUMessage
        } = this.props;
        const storeCreditNotEnough = storeCredits?.length > 0 && !isPaymentComplete;
        const hasGiftCardsApplied = giftCardPaymentGroups?.length > 0;
        const shouldDisplaySubscriptionError = (shouldDisplayAutoReplenMessage || shouldDisplaySDUMessage) && !this.state.openCreditCardForm;

        return (
            <>
                {(storeCreditNotEnough || shouldDisplaySubscriptionError) && (
                    <Text
                        is='p'
                        color='red'
                        children={
                            shouldDisplaySubscriptionError
                                ? isStoreCreditApplied
                                    ? localization.accountCreditSubscriptionMessage
                                    : localization.subscriptionCreditCardRequired
                                : hasGiftCardsApplied
                                    ? localization.storeCreditAndGCNotEnoughMessage
                                    : localization.storeCreditNotEnoughMessage
                        }
                        marginTop={4}
                        marginLeft={[4, 5]}
                    />
                )}
                <NewUserPaymentView
                    localization={localization}
                    availablePaymentMethods={newUserAvailablePaymentMethods}
                    paymentOptions={paymentOptions}
                    orderDetails={this.props.orderDetails}
                    installmentValue={this.props.installmentValue}
                    onAddNewCard={this.handleAddNewCard}
                    handleAfterPayExpand={() => this.handleAfterPayExpandFromL1({ isExpressPayment: false })}
                    handleKlarnaExpand={() => this.handleKlarnaExpandFromL1({ isExpressPayment: false })}
                    handlePayPalClick={() => this.handlePayPalExpandFromL1({ isExpressPayment: false })}
                    handleVenmoExpand={() => this.handleVenmoExpandFromL1({ isExpressPayment: false })}
                    handlePazeExpand={() => this.handlePazeExpandFromL1({ isExpressPayment: false })}
                    handlePayWithGooglePayClick={() => this.handleGooglePayExpandFromL1({ isExpressPayment: false })}
                    handlePayWithApplePayClick={this.handlePayWithApplePayClick}
                    giftCardPaymentGroups={giftCardPaymentGroups}
                    showGiftCardAdditionalPaymentRequired={!!gcAmountNotEnoughMessage && !storeCreditNotEnough}
                    giftCardAmountEqualsOrderTotal={giftCardAmountEqualsOrderTotal}
                    onGiftCardSaveAndContinue={this.handleSuccessfulPaymentUpdate}
                    isGuestCheckout={isGuestCheckout}
                    isGiftCardOnly={this.props.isGiftCardOnly}
                    withGiftCard={(!this.props.isGiftCardOnly && this.props.shouldRenderGiftCardSection) || this.props.isGiftCardPurchase}
                    onAlternatePaymentDetails={this.handleAlternatePaymentDetails}
                    isSubscriptionOrder={isSubscriptionOrder}
                    hasNoSavedCreditCards={hasNoSavedCreditCards}
                    paymentSectionHasError={!!sectionLevelError}
                    storeCredits={storeCredits}
                    isFullyCoveredByAccountCredit={isFullyCoveredByAccountCredit}
                    isStoreCreditApplied={isStoreCreditApplied}
                    physicalGiftCardShippingGroup={physicalGiftCardShippingGroup || this.props.isGiftCardPurchase}
                    paddingTop={storeCreditNotEnough || shouldDisplaySubscriptionError ? 4 : undefined}
                />
            </>
        );
    };

    renderEditView = () => {
        const {
            localization,
            giftCardPaymentGroups,
            shouldRenderGiftCardSection,
            availablePaymentMethods,
            alternateMethodSelected,
            alternatePaymentName,
            showErrorIcon,
            isGuestCheckout,
            zeroDollarHigherRedeemPointsCVV,
            zeroDollarHigherRedeemPointsNotCVV,
            paymentOptions,
            storeCredits,
            isPaymentComplete,
            orderHasPhysicalGiftCard,
            gcAmountNotEnoughMessage,
            isPhysicalGiftCardOnly,
            installmentValue,
            giftCardAmountEqualsOrderTotal,
            isSubscriptionOrder,
            hasNoSavedCreditCards,
            physicalGiftCardShippingGroup,
            hasSavedOrDefaultAlternatePayment,
            isGiftCardPurchase,
            sectionLevelError
        } = this.props;

        // L3 State: AfterPay Expanded View
        // This is rendered at the Payment level to work for both new users and existing users
        if (this.state.isAfterPayExpanded) {
            return (
                <Box paddingX={[4, 4, 5]}>
                    <AfterPayExpandedView
                        afterpayApiData={this.state.afterpayApiData}
                        onSaveAndContinue={this.handleAfterPaySaveAndContinue}
                        onCancel={this.handleAfterPayCancel}
                        loadWidget={this.loadAfterPayWidget}
                        afterpayHeader={localization.afterpayHeader}
                        afterpaySubtitle={localization.afterpaySubtitle}
                        afterpaySaveAndContinue={localization.afterpaySaveAndContinue}
                        afterpayCancel={localization.afterpayCancel}
                        afterpayLoading={localization.afterpayLoading}
                        afterpayApiErrorFallback={localization.afterpayApiErrorFallback}
                        afterpayGiftCardWarning={paymentUtils.isAfterpaySplitPaymentWithGCEnabled() ? '' : localization.afterpayGiftCardWarning}
                        afterpayLegalNotice={localization.afterpayLegalNotice}
                        isLoading={this.state.isAfterPayLoading}
                        errorMessage={this.state.afterpayError}
                        installmentValue={installmentValue}
                        isAnonymous={isGuestCheckout}
                    />
                </Box>
            );
        }

        if (this.state.shouldAutoExpandVenmo) {
            return (
                <Box paddingX={[4, 4, 5]}>
                    <VenmoExpandedView
                        venmoHeader={localization.venmoHeader}
                        venmoSaveAndContinue={localization.venmoSaveAndContinue}
                        venmoCancel={localization.cancel}
                        onSaveAndContinue={this.handleVenmoSaveAndContinue}
                        onCancel={this.clearAutoExpandVenmo}
                        isAnonymous={isGuestCheckout}
                    />
                </Box>
            );
        }

        // L3 State: PayPal Expanded View
        if (this.state.shouldAutoExpandPayPal) {
            return (
                <Box paddingX={[4, 4, 5]}>
                    <PayPalExpandedView
                        paypalHeader={localization.paypalHeader}
                        paypalLegalNotice={localization.paypalLegalNotice}
                        paypalSaveAndContinue={localization.paypalSaveAndContinue}
                        paypalCancel={localization.paypalCancel}
                        onSaveAndContinue={this.handlePayPalSaveAndContinue}
                        onCancel={this.clearAutoExpandPayPal}
                        isAnonymous={isGuestCheckout}
                    />
                </Box>
            );
        }

        // L3 State: Paze Expanded View
        // Handles Paze expansion for new users (no saved payment methods)
        // where shouldShowNewUserPaymentView() would otherwise prevent PaymentMethodList from rendering
        if (this.state.shouldAutoExpandPaze) {
            return (
                <Box paddingX={[4, 4, 5]}>
                    <PazeExpandedView
                        pazeHeader={localization.pazeHeader}
                        pazeSubtitle={localization.pazeSubtitle}
                        pazeGiftCardWarning={paymentUtils.isPazeSplitPaymentWithGCEnabled() ? '' : localization.pazeGiftCardWarning}
                        pazeSaveAndContinue={localization.pazeSaveAndContinue}
                        pazeCancel={localization.pazeCancel}
                        onSaveAndContinue={this.handlePazeSaveAndContinue}
                        onCancel={this.handlePazeCancel}
                    />
                </Box>
            );
        }

        // L3 State: Google Pay Expanded View
        // Rendered at the Payment level for guest/new users, mirroring the other alternate
        // payments — shouldShowNewUserPaymentView would otherwise keep PaymentMethodList unmounted.
        if (this.state.shouldAutoExpandGooglePay) {
            return (
                <Box paddingX={[4, 4, 5]}>
                    <GooglePayExpandedView
                        googlePayHeader={localization.googlePayHeader}
                        googlePayLegalNotice={localization.googlePayLegalNotice}
                        googlePaySaveAndContinue={localization.googlePaySaveAndContinue}
                        googlePayCancel={localization.cancel}
                        onSaveAndContinue={this.handleGooglePaySaveAndContinue}
                        onCancel={this.clearAutoExpandGooglePay}
                        isAnonymous={isGuestCheckout}
                    />
                </Box>
            );
        }

        // L3 State: Klarna Expanded View
        if (this.state.isKlarnaExpanded) {
            const { orderDetails } = this.props;
            const isAnonymous = checkoutUtils.isGuestOrder();
            const isBopisOrder = orderDetails?.header?.isBopisOrder;
            const isKlarnaSplitWithGCEnabled = paymentUtils.isKlarnaSplitPaymentWithGCEnabled();

            return (
                <Box paddingX={[4, 4, 5]}>
                    <KlarnaExpandedView
                        klarnaApiData={this.state.klarnaApiData}
                        onSaveAndContinue={this.handleKlarnaSaveAndContinue}
                        onCancel={this.handleKlarnaCancel}
                        loadWidget={this.loadKlarnaWidget}
                        klarnaHeader={localization.klarnaHeader}
                        klarnaSubtitle={localization.klarnaSubtitle}
                        klarnaSaveAndContinue={localization.klarnaSaveAndContinue}
                        klarnaCancel={localization.klarnaCancel}
                        klarnaLoading={localization.klarnaLoading}
                        klarnaApiErrorFallback={localization.klarnaApiErrorFallback}
                        klarnaGiftCardWarning={isKlarnaSplitWithGCEnabled ? '' : localization.klarnaGiftCardWarning}
                        klarnaFeatureBuyerProtection={localization.klarnaFeatureBuyerProtection}
                        klarnaTermsText={localization.klarnaTermsText}
                        klarnaBillingAddressCheckbox={localization.klarnaBillingAddressCheckbox}
                        isLoading={this.state.isKlarnaLoading}
                        errorMessage={this.state.klarnaError}
                        installmentValue={installmentValue}
                        isAnonymous={isAnonymous}
                        isBopisOrder={isBopisOrder}
                        useMyShippingAddress={this.state.useMyShippingAddress}
                        onBillingAddressToggle={this.handleBillingAddressToggle}
                    />
                </Box>
            );
        }

        const isFullyCoveredByAccountCredit = this.isAccountCreditCoveringFullOrder();
        const isStoreCreditApplied = this.isStoreCreditApplied();
        const shouldShowNewUserView = this.shouldShowNewUserPaymentView();
        const isAfterpayGCPurchaseEnabled = paymentUtils.isAfterpayGCPurchaseEnabled();
        const isAfterpayEnabledForThisOrder = isAfterpayGCPurchaseEnabled ? availablePaymentMethods.isAfterpayEnabledForThisOrder : false;
        const isAfterpayEnabledForThisProfile = isAfterpayGCPurchaseEnabled ? availablePaymentMethods.isAfterpayEnabledForThisProfile : false;
        const isKlarnaGCPurchaseEnabled = paymentUtils.isKlarnaGCPurchaseEnabled();
        const isKlarnaEnabledForThisOrder = isKlarnaGCPurchaseEnabled ? availablePaymentMethods.isKlarnaEnabledForThisOrder : false;

        // alternate payment tiles remain available in the Change/edit view even when a
        // gift card covers the order in full. Collapsed L1 suppression is handled in PaymentsDisplay.
        const newUserAvailablePaymentMethods = isGiftCardPurchase
            ? {
                ...availablePaymentMethods,
                isKlarnaEnabledForThisOrder,
                isAfterpayEnabledForThisOrder,
                isAfterpayEnabledForThisProfile,
                ...paymentUtils.getPazeGCPurchaseAvailability(availablePaymentMethods),
                ...paymentUtils.getPayPalGCPurchaseAvailability(availablePaymentMethods)
            }
            : availablePaymentMethods;

        if (shouldShowNewUserView) {
            return this.renderNewUserView(isFullyCoveredByAccountCredit, isStoreCreditApplied, newUserAvailablePaymentMethods);
        }

        // Show credit card form when user clicks Add New Card
        if (this.state.openCreditCardForm) {
            return (
                <Box paddingX={[4, 4, 5]}>
                    <CreditCardArea
                        handleCloseForm={this.handleCloseCreditCardForm}
                        callback={this.handleNewUserCreditCardSave}
                        isGuestCheckout={isGuestCheckout}
                        hideCancelButton={false}
                        editCreditCard={false}
                        isEditCCFlow={false}
                    />
                </Box>
            );
        }

        return (
            <Box paddingX={[4, 4, 5]}>
                <PaymentMethodList
                    {...availablePaymentMethods}
                    setEditChangeLink={this.setEditChangeLink}
                    alternateMethodSelected={alternateMethodSelected}
                    alternatePaymentName={alternatePaymentName}
                    showGiftCard={giftCardPaymentGroups.length < 2 && shouldRenderGiftCardSection}
                    showEditButtonFirst={showErrorIcon}
                    saveBtnSuccessCallback={this.handleSuccessfulPaymentUpdate}
                    isGuestCheckout={isGuestCheckout}
                    zeroDollarHigherRedeemPointsCVV={zeroDollarHigherRedeemPointsCVV}
                    openCreditCardForm={zeroDollarHigherRedeemPointsNotCVV && !paymentOptions?.creditCards?.length}
                    storeCredits={storeCredits}
                    isPaymentComplete={isPaymentComplete}
                    orderHasPhysicalGiftCard={orderHasPhysicalGiftCard}
                    showGCAmountNotEnoughMessage={!!gcAmountNotEnoughMessage}
                    isPhysicalGiftCardOnly={isPhysicalGiftCardOnly}
                    giftCardAmountEqualsOrderTotal={giftCardAmountEqualsOrderTotal}
                    shouldAutoExpandAfterpay={this.state.shouldAutoExpandAfterpay}
                    clearAutoExpandAfterpay={this.clearAutoExpandAfterpay}
                    installmentValue={this.props.installmentValue}
                    paymentLocales={localization}
                    shouldAutoExpandKlarna={this.state.shouldAutoExpandKlarna}
                    clearAutoExpandKlarna={this.clearAutoExpandKlarna}
                    shouldAutoExpandPayPal={this.state.shouldAutoExpandPayPal}
                    clearAutoExpandPayPal={this.clearAutoExpandPayPal}
                    shouldAutoExpandPaze={this.state.shouldAutoExpandPaze}
                    clearAutoExpandPaze={this.clearAutoExpandPaze}
                    shouldAutoExpandGooglePay={this.state.shouldAutoExpandGooglePay}
                    clearAutoExpandGooglePay={this.clearAutoExpandGooglePay}
                    clearPendingCreditCard={this.props.clearPendingCreditCard}
                    isSubscriptionOrder={isSubscriptionOrder}
                    hasNoSavedCreditCards={hasNoSavedCreditCards}
                    onAlternatePaymentDetails={this.handleAlternatePaymentDetails}
                    isFullyCoveredByAccountCredit={isFullyCoveredByAccountCredit}
                    isStoreCreditApplied={isStoreCreditApplied}
                    physicalGiftCardShippingGroup={physicalGiftCardShippingGroup}
                    hasSavedOrDefaultAlternatePayment={hasSavedOrDefaultAlternatePayment}
                    sectionLevelError={sectionLevelError}
                    paymentVerificationFailureRequested={this.props.paymentVerificationFailureRequested}
                    onExpiredSelectionChange={this.handleExpiredSelectionChange}
                />
            </Box>
        );
    };

    renderExclamationIconForError = () => (
        <Icon
            name='alert'
            color='red'
            size={[20, 23]}
            marginRight={2}
            aria-hidden={true}
        />
    );

    /* eslint-disable-next-line complexity */
    render() {
        const {
            sectionNumber,
            localization,
            isPaymentComplete,
            creditCardPaymentGroup,
            giftCardPaymentGroups,
            availablePaymentMethods,
            isPaypalSelected,
            payPalPaymentGroup,
            alternateMethodSelected,
            isNewUserFlow,
            storeCredits,
            canRedeemGiftCards,
            alternatePaymentName,
            installmentValue,
            isCombinableGiftCards,
            showErrorIcon,
            isGuestCheckout,
            venmoUsername,
            sectionLevelError,
            isEfullfillmentOrder,
            isPhysicalGiftCardAndHardGoodShippingGroup,
            isEditViewFromURL,
            orderDetails,
            gcAmountNotEnoughMessage,
            giftCardAmountEqualsOrderTotal,
            isSubscriptionOrder,
            hasNoSavedCreditCards,
            isGiftCardPurchase
        } = this.props;

        const { isGiftCardFormOpenOnL1, openCreditCardForm, hasSelectedExpiredCreditCard } = this.state;

        const {
            isAfterpayEnabledForThisOrder,
            isKlarnaEnabledForThisOrder,
            isVenmoEnabledForThisOrder,
            isPazeEnabledForThisOrder,
            isPayPalEnabled,
            isPayPalPayLaterEligible,
            isAfterpayEnabledForThisProfile
        } = availablePaymentMethods;
        const orderAmount = orderDetails?.priceInfo?.creditCardAmount || orderDetails?.priceInfo?.paypalAmount || 0;
        const isPaymentSectionCollapsed = this.shouldCollapsePaymentSection();
        const shouldRenderInlineGiftCardAdditionalPaymentWarning = sectionLevelError && sectionLevelError === gcAmountNotEnoughMessage;
        const isSubscriptionNoSavedCard = isSubscriptionOrder && hasNoSavedCreditCards;

        const isFullyCoveredByAccountCredit = this.isAccountCreditCoveringFullOrder();
        const isStoreCreditApplied = this.isStoreCreditApplied();

        // check for AC message so the header error icon stays in sync with the inline AC message
        const shouldTreatAccountCreditAsFinalPayment = isFullyCoveredByAccountCredit && !isSubscriptionOrder;
        const suppressACMessageForSubscription = isFullyCoveredByAccountCredit && isSubscriptionNoSavedCard;
        const isPaymentCompleteForAcError = checkoutUtils.isPaymentInOrderComplete(true);
        const showACMessage =
            (shouldTreatAccountCreditAsFinalPayment || (isStoreCreditApplied && !isPaymentCompleteForAcError)) && !suppressACMessageForSubscription;

        const shouldRenderHeaderErrorIconOnly = isSubscriptionNoSavedCard || (showACMessage && isEditViewFromURL);
        const shouldHidePaymentSectionError = shouldRenderInlineGiftCardAdditionalPaymentWarning || isSubscriptionNoSavedCard;
        const effectiveSectionLevelError = shouldHidePaymentSectionError ? null : sectionLevelError;

        // SHP-4190: When account credit covers full order in L2, always show "!"
        // SHP-4543: When store credit partially covers the order, show "!" while the
        //           "not enough credit" warning is visible, revert to section number once resolved
        const storeCreditNotEnough = storeCredits?.length > 0 && !isPaymentComplete;
        // Subscription orders with saved cards still require the user to confirm/select a card for recurring charges,
        // so full account credit should not be treated as a payment error in that specific scenario.
        const shouldTreatFullAccountCreditAsPaymentError = !(isSubscriptionOrder && !hasNoSavedCreditCards);
        // Only force the red exclamation when full account credit should actually behave as an error state.
        const showExclamationForFullCredit = isEditViewFromURL && isFullyCoveredByAccountCredit && shouldTreatFullAccountCreditAsPaymentError;
        const effectiveSectionNumber = storeCreditNotEnough || showExclamationForFullCredit ? this.renderExclamationIconForError() : sectionNumber;

        return (
            <LayoutCard
                sectionInfo={{
                    sectionNumber:
                        // SHP-4190: When account credit covers full order, always show the icon
                        // SHP-4390: But not when renderErrorView will already show one (subscription + no saved CC)
                        isEditViewFromURL &&
                        isFullyCoveredByAccountCredit &&
                        shouldTreatFullAccountCreditAsPaymentError &&
                        !shouldRenderHeaderErrorIconOnly
                            ? effectiveSectionNumber
                            : (showErrorIcon && !isNewUserFlow && !isGuestCheckout && !openCreditCardForm) ||
                                shouldRenderHeaderErrorIconOnly ||
                                (effectiveSectionLevelError && !shouldRenderInlineGiftCardAdditionalPaymentWarning) ||
                                (hasSelectedExpiredCreditCard && !openCreditCardForm)
                                ? null
                                : effectiveSectionNumber,
                    title: localization.cardTitle,
                    hasDivider: true,
                    isChangePermitted: !isEditViewFromURL && !isGiftCardFormOpenOnL1,
                    onChangeClick: this.setEditChangeLink,
                    renderErrorView:
                        (showErrorIcon && !isNewUserFlow && !isGuestCheckout && !openCreditCardForm) ||
                        shouldRenderHeaderErrorIconOnly ||
                        (!!effectiveSectionLevelError && !shouldRenderInlineGiftCardAdditionalPaymentWarning) ||
                        (hasSelectedExpiredCreditCard && !openCreditCardForm),
                    removeMarginTop: isEditViewFromURL,
                    sectionLevelError: effectiveSectionLevelError,
                    sectionName: SECTION_NAMES.PAYMENT
                }}
                marginTop={[4, 4, 5]}
                hasPaddingForChildren={false}
                isEditMode={isEditViewFromURL}
                isCollapsed={isPaymentSectionCollapsed}
                ariaLabel={localization.paymentInformation}
                role='region'
                sectionIcon={
                    <Flex flexDirection='row'>
                        <Icon
                            name='lock'
                            size='1.25em'
                            marginRight='.4em'
                            aria-hidden={true}
                        />
                        <Text color='gray'>{localization.secure}</Text>
                    </Flex>
                }
            >
                {/* Hidden container for legacy Klarna background initialization (old modal flow) */}
                {/* This prevents "InvalidContainerSelectorError" when commonOrderToggleActions dispatches backgroundInit */}
                <div
                    id='backgroundPaymentWidget'
                    style={{ display: 'none' }}
                />

                {/* Render L1 (collapsed/saved state) when not editing */}
                {/* Show PaymentsDisplay when: not in edit mode */}
                {/* Important: alternateMethodSelected keeps section expanded after Afterpay Save & Continue */}
                {!isEditViewFromURL ? (
                    <PaymentsDisplay
                        isPaymentComplete={isPaymentComplete}
                        creditCardPaymentGroup={creditCardPaymentGroup}
                        isPaypalSelected={isPaypalSelected}
                        payPalPaymentGroup={payPalPaymentGroup}
                        venmoUsername={venmoUsername}
                        storeCredits={storeCredits}
                        giftCardPaymentGroups={giftCardPaymentGroups}
                        canRedeemGiftCards={canRedeemGiftCards || !isCombinableGiftCards}
                        localization={localization}
                        alternatePaymentName={alternatePaymentName}
                        alternateMethodSelected={alternateMethodSelected}
                        checkoutEnabled={
                            isAfterpayEnabledForThisOrder || isKlarnaEnabledForThisOrder || isVenmoEnabledForThisOrder || isPazeEnabledForThisOrder
                        }
                        installmentValue={installmentValue}
                        showError={showErrorIcon}
                        isEfullfillmentOrder={isEfullfillmentOrder}
                        shouldRenderGiftCardSection={isPhysicalGiftCardAndHardGoodShippingGroup}
                        orderAmount={orderAmount}
                        isAfterpayEnabledForThisOrder={isAfterpayEnabledForThisOrder}
                        isKlarnaEnabledForThisOrder={isKlarnaEnabledForThisOrder}
                        isPayPalEnabled={isPayPalEnabled}
                        isPayPalPayLaterEligible={isPayPalPayLaterEligible}
                        isAfterpayEnabledForThisProfile={isAfterpayEnabledForThisProfile}
                        isPazeEnabledForThisOrder={isPazeEnabledForThisOrder}
                        handleAfterPayExpand={this.handleAfterPayExpandFromL1}
                        handleKlarnaExpand={this.handleKlarnaExpandFromL1}
                        handlePayPalClick={this.handlePayPalExpandFromL1}
                        handlePazeExpand={this.handlePazeExpandFromL1}
                        onGiftCardFormVisibilityChange={this.handleGiftCardFormVisibilityOnL1}
                        isGiftCardFormOpen={isGiftCardFormOpenOnL1}
                        showGiftCardAdditionalPaymentRequired={!!gcAmountNotEnoughMessage}
                        giftCardAmountEqualsOrderTotal={giftCardAmountEqualsOrderTotal}
                        isGuestCheckout={isGuestCheckout}
                        isSubscriptionOrder={isSubscriptionOrder}
                        isZeroDollarOrderWithCVVValidation={this.props.isZeroDollarOrderWithCVVValidation}
                        isFullyCoveredByAccountCredit={isFullyCoveredByAccountCredit}
                        isStoreCreditApplied={isStoreCreditApplied}
                        isGiftCardPurchase={isGiftCardPurchase}
                        isPayPalCheckoutEnabledForLocale={PayPalCheckout.isPayPalCheckoutEnabledForLocale()}
                        onAlternatePaymentDetails={this.handleAlternatePaymentDetails}
                    />
                ) : (
                    this.renderEditView()
                )}
            </LayoutCard>
        );
    }
}

export default wrapComponent(Payment, 'Payment', true);
